import React, { useState } from "react";
import "./AdminAutoImport.css";

import { db } from "../firebase";

import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";

const TIMEZONE = "Africa/Dar_es_Salaam";

/* =========================================================
   DEFAULT CORRECT SCORES + ODDS
========================================================= */

const DEFAULT_CORRECT_SCORES = [
  { score: "0 - 0", odds: 8.45 },
  { score: "1 - 0", odds: 4.85 },
  { score: "0 - 1", odds: 5.26 },
  { score: "1 - 1", odds: 1.87 },
  { score: "2 - 1", odds: 2.02 },
  { score: "1 - 2", odds: 2.08 },
  { score: "0 - 2", odds: 4.18 },
  { score: "2 - 0", odds: 2.25 },
  { score: "2 - 2", odds: 1.95 },
  { score: "0 - 3", odds: 3.34 },
  { score: "3 - 0", odds: 2.38 },
  { score: "3 - 3", odds: 1.1 },
  { score: "2 - 3", odds: 4.63 },
  { score: "3 - 2", odds: 1.05 },
  { score: "1 - 3", odds: 7.38 },
  { score: "3 - 1", odds: 2.13 },
];

/* =========================================================
   DATE HELPERS
========================================================= */

function getTZDate(addDays = 0) {
  const d = new Date();

  d.setDate(d.getDate() + addDays);

  return new Intl.DateTimeFormat("en-CA", {
    timeZone: TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

/* =========================================================
   NORMALIZE DATE
========================================================= */

function normalizeDate(dateValue) {
  if (!dateValue) {
    return "";
  }

  const value = String(dateValue).trim();

  if (!value) {
    return "";
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en-CA", {
    timeZone: TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(parsed);
}

/* =========================================================
   NORMALIZE TIME
========================================================= */

function normalizeTime(timeValue) {
  if (!timeValue) {
    return "";
  }

  const value = String(timeValue).trim();

  if (!value) {
    return "";
  }

  const match = value.match(
    /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/
  );

  if (!match) {
    return value;
  }

  const hours = String(
    Math.min(23, Math.max(0, Number(match[1])))
  ).padStart(2, "0");

  const minutes = String(
    Math.min(59, Math.max(0, Number(match[2])))
  ).padStart(2, "0");

  return `${hours}:${minutes}`;
}

/* =========================================================
   GET DATE BETWEEN
========================================================= */

function getDatesBetween(start, end) {
  const dates = [];

  if (!start || !end) {
    return dates;
  }

  const current = new Date(`${start}T00:00:00`);
  const last = new Date(`${end}T00:00:00`);

  if (
    Number.isNaN(current.getTime()) ||
    Number.isNaN(last.getTime())
  ) {
    return dates;
  }

  while (current <= last) {
    dates.push(
      current.toISOString().slice(0, 10)
    );

    current.setDate(
      current.getDate() + 1
    );
  }

  return dates;
}

/* =========================================================
   TIME TO MINUTES
========================================================= */

function convertToMinutes(time) {
  if (!time) {
    return null;
  }

  const parts = String(time)
    .split(":")
    .map(Number);

  if (parts.length < 2) {
    return null;
  }

  const hours = parts[0];
  const minutes = parts[1];

  if (
    !Number.isFinite(hours) ||
    !Number.isFinite(minutes)
  ) {
    return null;
  }

  return hours * 60 + minutes;
}

/* =========================================================
   FORMAT MANUAL MATCH
========================================================= */

function formatManualMatch(m) {
  const dateOnly = normalizeDate(
    m.dateOnly ||
      m.date ||
      m.matchDate ||
      ""
  );

  const time = normalizeTime(
    m.time ||
      m.matchTime ||
      ""
  );

  const A =
    m.A ||
    m.homeTeam ||
    m.home ||
    m.teamA ||
    "";

  const B =
    m.B ||
    m.awayTeam ||
    m.away ||
    m.teamB ||
    "";

  const league =
    m.league ||
    m.tournament ||
    m.competition ||
    "League";

  const country =
    m.country ||
    "Other";

  const logoA =
    m.logoA ||
    m.homeLogo ||
    "";

  const logoB =
    m.logoB ||
    m.awayLogo ||
    "";

  const status =
    m.status ||
    "notstarted";

  const result =
    m.result ||
    "pending";

  const startTimestamp =
    Number(m.startTimestamp) ||
    0;

  return {
    A: String(A).trim(),

    B: String(B).trim(),

    league: String(league).trim(),

    country: String(country).trim(),

    dateOnly,

    time,

    date:
      dateOnly && time
        ? `${dateOnly} ${time}:00`
        : dateOnly || "",

    logoA: String(logoA).trim(),

    logoB: String(logoB).trim(),

    timezone:
      m.timezone ||
      TIMEZONE,

    source:
      m.source ||
      "manual",

    result,

    status,

    sofaId:
      m.sofaId ||
      m.sofascoreId ||
      null,

    startTimestamp,

    correctScores:
      Array.isArray(m.correctScores) &&
      m.correctScores.length > 0
        ? m.correctScores
        : DEFAULT_CORRECT_SCORES,
  };
}

/* =========================================================
   COMPONENT
========================================================= */

export default function AdminAutoImport() {
  const [matches, setMatches] = useState([]);

  const [loading, setLoading] =
    useState(false);

  const [uploading, setUploading] =
    useState(false);

  const [mode, setMode] =
    useState("single");

  const [manualJson, setManualJson] =
    useState("");

  const [date, setDate] =
    useState(getTZDate(0));

  const [fromDate, setFromDate] =
    useState(getTZDate(0));

  const [toDate, setToDate] =
    useState(getTZDate(0));

  const [search, setSearch] =
    useState("");

  const [limit, setLimit] =
    useState(99999);

  const [startTime, setStartTime] =
    useState("");

  const [endTime, setEndTime] =
    useState("");

  const [openCountries, setOpenCountries] =
    useState({});

  const [openLeagues, setOpenLeagues] =
    useState({});

  /* =========================================================
     MANUAL JSON IMPORT
  ========================================================= */

  const importManualMatches = async () => {
    try {
      if (!manualJson.trim()) {
        alert("Bandika JSON kwanza ❌");
        return;
      }

      const parsed =
        JSON.parse(manualJson);

      if (!Array.isArray(parsed)) {
        alert(
          "JSON lazima iwe array []"
        );
        return;
      }

      if (parsed.length === 0) {
        alert(
          "JSON array haina matches."
        );
        return;
      }

      setUploading(true);

      let uploaded = 0;
      let skipped = 0;

      for (const rawMatch of parsed) {
        const match =
          formatManualMatch(
            rawMatch
          );

        if (
          !match.A ||
          !match.B
        ) {
          skipped++;
          continue;
        }

        const exists =
          await isDuplicate(
            match
          );

        if (exists) {
          skipped++;
          continue;
        }

        await addDoc(
          collection(
            db,
            "matches"
          ),
          {
            ...match,

            correctScores:
              DEFAULT_CORRECT_SCORES,

            uploadedAt:
              serverTimestamp(),
          }
        );

        uploaded++;
      }

      alert(
        `${uploaded} matches imported ✅\n${skipped} skipped`
      );

      setManualJson("");

    } catch (err) {
      console.error(
        "Manual JSON import error:",
        err
      );

      alert(
        "JSON Error ❌\nHakikisha JSON ni sahihi."
      );
    } finally {
      setUploading(false);
    }
  };

  /* =========================================================
     TIME FILTER
  ========================================================= */

  const isWithinTimeRange =
    (matchTime) => {
      if (
        !startTime &&
        !endTime
      ) {
        return true;
      }

      const matchMinutes =
        convertToMinutes(
          matchTime
        );

      const startMinutes =
        convertToMinutes(
          startTime
        );

      const endMinutes =
        convertToMinutes(
          endTime
        );

      if (
        matchMinutes === null
      ) {
        return false;
      }

      if (
        startTime &&
        !endTime
      ) {
        return (
          matchMinutes >=
          startMinutes
        );
      }

      if (
        !startTime &&
        endTime
      ) {
        return (
          matchMinutes <=
          endMinutes
        );
      }

      return (
        matchMinutes >=
          startMinutes &&
        matchMinutes <=
          endMinutes
      );
    };

  /* =========================================================
     FILTER MATCHES
  ========================================================= */

  const filteredMatches =
    matches
      .filter((m) => {
        const q =
          search
            .toLowerCase()
            .trim();

        if (!q) {
          return true;
        }

        return (
          String(m.A)
            .toLowerCase()
            .includes(q) ||

          String(m.B)
            .toLowerCase()
            .includes(q) ||

          String(m.league)
            .toLowerCase()
            .includes(q) ||

          String(m.country)
            .toLowerCase()
            .includes(q)
        );
      })

      .filter((m) =>
        isWithinTimeRange(
          m.time
        )
      )

      .slice(
        0,
        Number(limit) || 0
      );

  /* =========================================================
     GROUP MATCHES
  ========================================================= */

  const filteredGrouped = {};

  filteredMatches.forEach(
    (m) => {
      const country =
        m.country ||
        "Other";

      const league =
        m.league ||
        "League";

      if (
        !filteredGrouped[
          country
        ]
      ) {
        filteredGrouped[
          country
        ] = {};
      }

      if (
        !filteredGrouped[
          country
        ][league]
      ) {
        filteredGrouped[
          country
        ][league] = [];
      }

      filteredGrouped[
        country
      ][league].push(m);
    }
  );

  /* =========================================================
     DUPLICATE CHECK
  ========================================================= */

  const isDuplicate =
    async (match) => {
      try {
        /* -----------------------------------------------------
           SOFASCORE ID CHECK
           Only for old/imported records that contain one.
           No API is called.
        ----------------------------------------------------- */

        if (match.sofaId) {
          const sofaQuery =
            query(
              collection(
                db,
                "matches"
              ),
              where(
                "sofaId",
                "==",
                match.sofaId
              )
            );

          const sofaSnap =
            await getDocs(
              sofaQuery
            );

          if (
            !sofaSnap.empty
          ) {
            return true;
          }
        }

        /* -----------------------------------------------------
           TEAM + DATE CHECK
        ----------------------------------------------------- */

        if (
          match.A &&
          match.B &&
          match.date
        ) {
          const qMatch =
            query(
              collection(
                db,
                "matches"
              ),

              where(
                "A",
                "==",
                match.A
              ),

              where(
                "B",
                "==",
                match.B
              ),

              where(
                "date",
                "==",
                match.date
              )
            );

          const snap =
            await getDocs(
              qMatch
            );

          if (
            !snap.empty
          ) {
            return true;
          }
        }

        return false;

      } catch (err) {
        console.error(
          "Duplicate check error:",
          err
        );

        return false;
      }
    };

  /* =========================================================
     UPLOAD FILTERED MATCHES
  ========================================================= */

  const uploadMatches =
    async () => {
      try {
        if (
          filteredMatches.length ===
          0
        ) {
          alert(
            "Hakuna matches za ku-upload."
          );
          return;
        }

        setUploading(true);

        let count = 0;
        let skipped = 0;

        for (
          const m of filteredMatches
        ) {
          const exists =
            await isDuplicate(
              m
            );

          if (exists) {
            skipped++;
            continue;
          }

          await addDoc(
            collection(
              db,
              "matches"
            ),
            {
              ...m,

              correctScores:
                DEFAULT_CORRECT_SCORES,

              uploadedAt:
                serverTimestamp(),
            }
          );

          count++;
        }

        alert(
          `${count} matches uploaded 🚀\n${skipped} duplicates skipped`
        );

      } catch (err) {
        console.error(
          "Upload error:",
          err
        );

        alert(
          "Upload error ❌"
        );
      } finally {
        setUploading(false);
      }
    };

  /* =========================================================
     CLEAR MATCHES
  ========================================================= */

  const clearMatches = () => {
    setMatches([]);
    setOpenCountries({});
    setOpenLeagues({});
  };

  /* =========================================================
     LOAD JSON INTO PREVIEW
  ========================================================= */

  const previewManualJson =
    () => {
      try {
        if (
          !manualJson.trim()
        ) {
          alert(
            "Bandika JSON kwanza."
          );
          return;
        }

        const parsed =
          JSON.parse(
            manualJson
          );

        if (
          !Array.isArray(parsed)
        ) {
          alert(
            "JSON lazima iwe array []."
          );
          return;
        }

        const formatted =
          parsed
            .map(
              formatManualMatch
            )
            .filter(
              (m) =>
                m.A &&
                m.B
            );

        setMatches(
          formatted
        );

        setOpenCountries({});
        setOpenLeagues({});

        alert(
          `${formatted.length} matches loaded kwenye preview ✅`
        );

      } catch (err) {
        console.error(
          "Preview JSON error:",
          err
        );

        alert(
          "JSON Error ❌"
        );
      }
    };

  /* =========================================================
     RETURN
  ========================================================= */

  return (
    <div className="container">

      <h2 className="title">
        🔥 Smart Admin Matches
      </h2>

      <div
        style={{
          padding: "8px 0",
          fontWeight: "bold",
          color: "#22c55e",
        }}
      >
        Timezone: East Africa Time (EAT)
      </div>

      {/* =====================================================
          SOURCE INFORMATION
      ===================================================== */}

      <div
        style={{
          padding: "12px",
          marginBottom: "12px",
          borderRadius: "8px",
          background:
            "rgba(34,197,94,0.08)",
          border:
            "1px solid rgba(34,197,94,0.25)",
          color: "#22c55e",
          fontSize: "13px",
        }}
      >
        Manual mode only — no
        football API or SofaScore
        connection.
      </div>

      {/* =====================================================
          MODE
      ===================================================== */}

      <select
        className="input"
        value={mode}
        onChange={(e) =>
          setMode(
            e.target.value
          )
        }
      >
        <option value="single">
          Single Date
        </option>

        <option value="range">
          Select Range - Date to Date
        </option>
      </select>

      {/* =====================================================
          SINGLE DATE
      ===================================================== */}

      {mode === "single" && (
        <>
          <div
            style={{
              display: "flex",
              gap: 10,
            }}
          >
            <button
              type="button"
              onClick={() =>
                setDate(
                  getTZDate(0)
                )
              }
            >
              Today
            </button>

            <button
              type="button"
              onClick={() =>
                setDate(
                  getTZDate(1)
                )
              }
            >
              Tomorrow
            </button>
          </div>

          <br />

          <input
            type="date"
            value={date}
            onChange={(e) =>
              setDate(
                e.target.value
              )
            }
            className="input"
          />
        </>
      )}

      {/* =====================================================
          RANGE
      ===================================================== */}

      {mode === "range" && (
        <>
          <input
            type="date"
            value={fromDate}
            onChange={(e) =>
              setFromDate(
                e.target.value
              )
            }
            className="input"
          />

          <input
            type="date"
            value={toDate}
            onChange={(e) =>
              setToDate(
                e.target.value
              )
            }
            className="input"
          />

          <div
            style={{
              fontSize: "12px",
              opacity: 0.7,
              marginBottom: "10px",
            }}
          >
            Selected range:{" "}
            {getDatesBetween(
              fromDate,
              toDate
            ).length}{" "}
            day(s)
          </div>
        </>
      )}

      {/* =====================================================
          SEARCH
      ===================================================== */}

      <input
        type="text"
        placeholder="Search team, league or country..."
        value={search}
        onChange={(e) =>
          setSearch(
            e.target.value
          )
        }
        className="input"
      />

      {/* =====================================================
          TIME FILTER
      ===================================================== */}

      <div
        style={{
          display: "flex",
          gap: 10,
        }}
      >
        <input
          type="time"
          value={startTime}
          onChange={(e) =>
            setStartTime(
              e.target.value
            )
          }
          className="input"
        />

        <input
          type="time"
          value={endTime}
          onChange={(e) =>
            setEndTime(
              e.target.value
            )
          }
          className="input"
        />
      </div>

      {/* =====================================================
          LIMIT
      ===================================================== */}

      <select
        value={limit}
        onChange={(e) =>
          setLimit(
            Number(
              e.target.value
            )
          )
        }
        className="input"
      >
        <option value={10}>
          Select 10 matches
        </option>

        <option value={20}>
          Select 20 matches
        </option>

        <option value={30}>
          Select 30 matches
        </option>

        <option value={40}>
          Select 40 matches
        </option>

        <option value={50}>
          Select 50 matches
        </option>

        <option value={100}>
          Select 100 matches
        </option>

        <option value={300}>
          Select 300 matches
        </option>

        <option value={500}>
          Select 500 matches
        </option>

        <option value={99999}>
          All Matches
        </option>
      </select>

      {/* =====================================================
          MANUAL JSON
      ===================================================== */}

      <textarea
        value={manualJson}
        onChange={(e) =>
          setManualJson(
            e.target.value
          )
        }
        placeholder={`Paste JSON array here...

Example:
[
  {
    "A": "Arsenal",
    "B": "Chelsea",
    "league": "Premier League",
    "country": "England",
    "dateOnly": "2026-10-07",
    "time": "20:00",
    "logoA": "",
    "logoB": ""
  }
]`}
        className="input"
        style={{
          minHeight: "250px",
          width: "100%",
          marginTop: "10px",
          resize: "vertical",
        }}
      />

      {/* =====================================================
          JSON BUTTONS
      ===================================================== */}

      <div
        style={{
          display: "flex",
          gap: "10px",
          flexWrap: "wrap",
          marginTop: "10px",
        }}
      >
        <button
          type="button"
          onClick={
            previewManualJson
          }
          className="button"
        >
          👁️ Preview JSON
        </button>

        <button
          type="button"
          onClick={
            importManualMatches
          }
          className="uploadBtn"
          disabled={uploading}
        >
          {uploading
            ? "Importing..."
            : "🚀 Import JSON"}
        </button>
      </div>

      {/* =====================================================
          ACTION BUTTONS
      ===================================================== */}

      <div
        style={{
          display: "flex",
          gap: "10px",
          flexWrap: "wrap",
          marginTop: "10px",
        }}
      >
        <button
          type="button"
          onClick={
            uploadMatches
          }
          className="uploadBtn"
          disabled={
            uploading ||
            filteredMatches.length ===
              0
          }
        >
          {uploading
            ? "Uploading..."
            : "🚀 Upload Filtered"}
        </button>

        <button
          type="button"
          onClick={
            clearMatches
          }
        >
          🗑️ Clear
        </button>
      </div>

      {/* =====================================================
          COUNT
      ===================================================== */}

      <div
        style={{
          padding: "10px 0",
          fontWeight: "bold",
        }}
      >
        Selected Matches:{" "}
        {filteredMatches.length}
      </div>

      <br />

      {/* =====================================================
          MATCHES
      ===================================================== */}

      {Object.keys(
        filteredGrouped
      ).length === 0 && (
        <div
          style={{
            padding: "20px",
            textAlign: "center",
            opacity: 0.7,
          }}
        >
          No matches loaded.
          <br />
          Paste your JSON above and
          click Preview JSON.
        </div>
      )}

      {Object.keys(
        filteredGrouped
      ).map((country) => (
        <div
          key={country}
          className="countryBlock"
        >
          {/* COUNTRY */}

          <div
            className="countryHeader"
            onClick={() =>
              setOpenCountries(
                (prev) => ({
                  ...prev,

                  [country]:
                    !prev[
                      country
                    ],
                })
              )
            }
          >
            🌍 {country}
          </div>

          {/* LEAGUES */}

          {openCountries[
            country
          ] &&
            Object.keys(
              filteredGrouped[
                country
              ]
            ).map((league) => {
              const leagueMatches =
                filteredGrouped[
                  country
                ][league];

              const leagueKey =
                `${country}-${league}`;

              return (
                <div
                  key={leagueKey}
                  className="leagueBlock"
                >
                  <div
                    className="leagueHeader"
                    onClick={() =>
                      setOpenLeagues(
                        (prev) => ({
                          ...prev,

                          [leagueKey]:
                            !prev[
                              leagueKey
                            ],
                        })
                      )
                    }
                  >
                    🏆 {league} (
                    {
                      leagueMatches.length
                    }
                    )
                  </div>

                  {/* MATCH LIST */}

                  {openLeagues[
                    leagueKey
                  ] &&
                    leagueMatches.map(
                      (m, i) => (
                        <div
                          key={
                            m.sofaId ||
                            `${m.A}-${m.B}-${m.date}-${i}`
                          }
                          className="matchRow"
                        >
                          {/* HOME */}

                          <div className="team">
                            {m.logoA && (
                              <img
                                src={
                                  m.logoA
                                }
                                alt=""
                              />
                            )}

                            <span>
                              {m.A}
                            </span>
                          </div>

                          {/* TIME */}

                          <div className="vs">
                            <div>
                              {m.time ||
                                "--:--"}
                            </div>

                            <small>
                              {m.dateOnly ||
                                ""}
                            </small>
                          </div>

                          {/* AWAY */}

                          <div className="team">
                            {m.logoB && (
                              <img
                                src={
                                  m.logoB
                                }
                                alt=""
                              />
                            )}

                            <span>
                              {m.B}
                            </span>
                          </div>
                        </div>
                      )
                    )}
                </div>
              );
            })}
        </div>
      ))}
    </div>
  );
}